import { useState } from "react";
import type { ModulePreset } from "@cliqsuite/db";
import { useModulePresets } from "../../lib/modulePresets";

export const PLATFORM_DEFS = [
  { key: "pos" as const, label: "Retail & food", icon: "storefront", desc: "Sell products: café, restaurant, shop", disabled: false },
  { key: "service" as const, label: "Service & appointments", icon: "event_available", desc: "Book time: salon, spa, gym, clinic", disabled: true },
];

/** Step "What do you run?": the two platform cards. */
export function PlatformChoice({ value, onChange }: { value: "pos" | "service"; onChange: (p: "pos" | "service") => void }) {
  return (
    <div className="o-choose" role="radiogroup" aria-label="What do you run">
      {PLATFORM_DEFS.map((p) => (
        <button type="button" role="radio" aria-checked={value === p.key} aria-disabled={p.disabled} key={p.key} onClick={() => !p.disabled && onChange(p.key)}>
          <span className="o-ico"><span className="ms" aria-hidden="true">{p.icon}</span></span>
          <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <strong>{p.label}</strong>
            <span>{p.disabled ? "Coming soon" : p.desc}</span>
          </span>
          <span className="ms o-choose__tick" aria-hidden="true">check_circle</span>
        </button>
      ))}
    </div>
  );
}

/** Step "What kind of business?": the preset grid, plus which modules the pick switches on. */
export function KindChoice({ presets, value, onChange }: { presets: ModulePreset[]; value: string | null; onChange: (key: string) => void }) {
  const picked = presets.find((p) => p.key === value);
  return (
    <>
      <div className="o-kinds" role="radiogroup" aria-label="Kind of business">
        {presets.map((b) => (
          <button type="button" role="radio" aria-checked={value === b.key} key={b.key} onClick={() => onChange(b.key)}>
            <span className="ms" aria-hidden="true">{b.icon}</span>
            {b.label}
          </button>
        ))}
      </div>
      {picked && (
        <div className="o-note" style={{ marginTop: 0 }}>
          <span className="ms" aria-hidden="true">widgets</span>
          <span>
            {picked.label} turns on {picked.modules.length} modules. You can change them later.
          </span>
        </div>
      )}
    </>
  );
}

export interface BusinessTypePickerProps {
  /** Selected preset key (e.g. "cafe", "salon"), or null if nothing chosen yet. */
  value: string | null;
  onChange: (presetKey: string) => void;
}

/** Both steps in one, for FirstStoreGate. */
export function BusinessTypePicker({ value, onChange }: BusinessTypePickerProps) {
  const { presets } = useModulePresets();
  const selected = value ? presets.find((p) => p.key === value) : null;
  const [platformOverride, setPlatformOverride] = useState<"pos" | "service" | null>(null);
  const platform = platformOverride ?? selected?.platform ?? "pos";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PlatformChoice
        value={platform}
        onChange={(p) => {
          setPlatformOverride(p);
          const first = presets.find((x) => x.platform === p);
          if (first) onChange(first.key);
        }}
      />
      <KindChoice presets={presets.filter((p) => p.platform === platform)} value={value} onChange={onChange} />
    </div>
  );
}
